import React, {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { IconButton, InputAdornment, TextField, Tooltip } from "@mui/material";
import { StyleSettings } from "../../styles/StyleSettings";
import { onSearchStore } from "@shared/libs/Global";
import { ClearIcon } from "@mui/x-date-pickers";

interface SnBarcodeBoxProps {
  defaultValue?: any;
  onValueChanged?: (value: string) => void;
  format?: string;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isEditable?: boolean;
  popupRef?: any;
  inputRef?: any;
  isSearchPanel?: boolean;
  onFocus?: (target: string) => void;
  onEnter?: (value: string) => Promise<void> | void;
  isAutoClear?: boolean;
}

// React.memo와 forwardRef를 결합하여 외부 ref 접근을 허용합니다.
const SnBarcodeBox = React.memo(
  forwardRef<any, SnBarcodeBoxProps>((props, ref) => {
    const {
      defaultValue = "",
      onValueChanged,
      format,
      isReadOnly = false,
      isRequired,
      isEditable = true,
      popupRef,
      inputRef,
      isSearchPanel = false,
      onFocus,
      onEnter,
      isAutoClear = true,
    } = props;

    // 초기값 format 적용
    const getInitialValue = () => {
      let safeDefault = defaultValue === null ? "" : defaultValue;
      return safeDefault;
    };

    const [value, setValue] = useState(getInitialValue());
    const [borderWidth, setBorderWidth] = useState("1px");
    const [backgroundColor, setBackgroundColor] = useState("#F1F1F1");

    // 한/영 키가 한글 상태인지 감지 (첫 입력 시점에 판별)
    // - 브라우저에는 IME 상태를 직접 읽는 API가 없어서, 조합 중인 글자 또는 입력값에
    //   한글이 섞여 들어오는지를 증거로 사용한다. (데스크톱/태블릿 공통)
    // - 조합 이벤트 유무로 판단하면 Android 키보드 자동완성에서 오탐이 나므로
    //   반드시 "한글 문자 포함 여부"로만 판단한다.
    // - 한/영을 다시 눌렀는지는 확실히 알 수 없으므로(다른 곳에 포커스가 있을 때 누르면 이벤트가 없다)
    //   경고는 한 번 띄운 뒤 일정 시간이 지나면 자동으로 내리고, 포커스가 빠지면 초기화한다.
    const [isHangulIme, setIsHangulIme] = useState(false);
    const HANGUL_REGEX = /[ㄱ-ㅎㅏ-ㅣ가-힣]/;
    const HANGUL_WARN_MS = 3000;
    const warnTimerRef = React.useRef<number | null>(null);

    const clearHangulWarning = () => {
      if (warnTimerRef.current) {
        window.clearTimeout(warnTimerRef.current);
        warnTimerRef.current = null;
      }
      setIsHangulIme(false);
    };

    // 한글이 들어올 때마다 타이머를 다시 걸어, 스캔이 이어지는 동안은 유지되고 끝나면 내려간다
    const showHangulWarning = () => {
      setIsHangulIme(true);
      if (warnTimerRef.current) window.clearTimeout(warnTimerRef.current);
      warnTimerRef.current = window.setTimeout(() => {
        warnTimerRef.current = null;
        setIsHangulIme(false);
      }, HANGUL_WARN_MS);
    };

    // 컴포넌트가 사라질 때 타이머 정리
    useEffect(
      () => () => {
        if (warnTimerRef.current) window.clearTimeout(warnTimerRef.current);
      },
      []
    );

    // 외부에서 접근 가능한 메서드 정의
    useImperativeHandle(ref, () => ({
      clear: () => {
        setValue("");
      },
      // 현재 값을 외부에서 바로 읽을 때 사용
      getValue: () => value,
      focus: () => inputRef.current?.focus(),
    }));

    // SearchPanel에서 enter 누르면 실행할 함수

    const fetchDefaultValue = async () => {
      try {
        const safeDefaultValue = defaultValue === null ? "" : defaultValue;
        const newFormattedValue = safeDefaultValue;

        if (value !== newFormattedValue) {
          setValue(newFormattedValue);
        }
      } catch (error: any) {}
    };

    const fetchIsRequired = async () => {
      try {
        if (isRequired) setBorderWidth("2px");
        else setBorderWidth("1px");
      } catch (error: any) {}
    };

    const fetchIsEditable = async () => {
      try {
        if (isEditable) setBackgroundColor("white");
        else setBackgroundColor("#F1F1F1");
      } catch (error: any) {}
    };

    useEffect(() => {
      fetchDefaultValue();
    }, [defaultValue]);

    useEffect(() => {
      fetchIsRequired();
    }, [isRequired]);

    useEffect(() => {
      fetchIsEditable();
    }, [isEditable]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
      // 조합이 끝나고 확정된 값에 한글이 남아있는 경우도 감지 (태블릿 등)
      if (HANGUL_REGEX.test(e.target.value)) showHangulWarning();
    };

    // 한글 IME는 첫 키 입력 시 compositionupdate 로 "ㅁ" 같은 자모를 먼저 넘겨준다.
    // 스캐너가 바코드를 다 쏟아내기 전, 첫 글자 시점에 경고를 띄우기 위해 여기서 잡는다.
    const handleCompositionUpdate = (
      e: React.CompositionEvent<HTMLInputElement>
    ) => {
      if (HANGUL_REGEX.test(e.data ?? "")) showHangulWarning();
    };

    /**
     * 한글이 섞인 스캔은 바코드가 깨진 것이므로 스캔 로직(onEnter)을 태우지 않는다.
     * 경고를 띄우고 값은 비워서 한/영 전환 후 바로 다시 스캔할 수 있게 한다.
     * (조합 확정 직후에 비워야 IME 가 남은 글자를 다시 넣지 않으므로 다음 틱에 비운다)
     */
    const rejectHangulScan = () => {
      showHangulWarning();
      window.setTimeout(() => {
        setValue("");
        inputRef?.current?.focus();
      }, 0);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      // 한글 상태에서 들어온 Enter: IME 가 조합 확정으로 삼켜 form submit 이 안 뜨는 경우가 있어
      // keydown 에서 먼저 잡아 스캔 로직을 차단한다 (조합 중에는 e.key 가 "Process" 라 e.code 로 판별)
      if (
        (e.key === "Enter" || e.code === "Enter") &&
        HANGUL_REGEX.test(value)
      ) {
        e.preventDefault();
        e.stopPropagation();
        rejectHangulScan();
        return;
      }
      // 영문 알파벳이 그대로 들어오면 IME가 영문 상태라는 확정 증거 → 즉시 해제
      if (e.key.length === 1 && /[A-Za-z]/.test(e.key)) {
        clearHangulWarning();
        return;
      }
      // 한/영 키(HangulMode, keyCode 21)를 눌렀으면 전환된 것으로 보고 즉시 해제
      if (e.key === "HangulMode" || e.keyCode === 21) {
        clearHangulWarning();
      }
    };

    // 포커스가 빠지면 경고 초기화 (다음 포커스 때는 새 입력으로 다시 판단)
    const handleBlur = () => clearHangulWarning();

    // const handleKeyDown = async (e: KeyboardEvent<HTMLInputElement>) => {
    //   if (e.key === "Enter") {
    //     e.preventDefault();
    //     e.stopPropagation();
    //     await onEnter?.(value);
    //     setValue("");
    //     inputRef.current?.focus();
    //   }
    // };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault(); // 태블릿 브라우저의 페이지 새로고침 방지 (필수!)
      e.stopPropagation();

      if (!value) return; // 빈 값일 때는 실행 방지

      // 한글이 섞였으면 깨진 바코드 → 스캔 로직 차단
      if (HANGUL_REGEX.test(value)) {
        rejectHangulScan();
        return;
      }

      await onEnter?.(value);
      if (isAutoClear) {
        setValue("");
        inputRef.current?.focus();
      }
    };

    return (
      <form onSubmit={handleSubmit} style={{ width: "100%" }}>
        <Tooltip
          // 한글 IME 감지 시 일정 시간 표시 (레이아웃에 영향 없도록 helperText 대신 Tooltip 사용)
          open={isHangulIme}
          title="한/영 키가 한글 상태입니다. 영문으로 전환 후 다시 스캔하세요."
          placement="bottom-start"
          arrow
          disableHoverListener
          disableFocusListener
          disableTouchListener
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: "#d32f2f",
                fontSize: StyleSettings.font,
                fontWeight: 600,
              },
            },
            arrow: { sx: { color: "#d32f2f" } },
          }}
        >
          <TextField
            inputRef={inputRef}
            autoFocus={popupRef && true}
            placeholder="SCAN HERE"
            id="BarcodeScan"
            value={value}
            error={isHangulIme}
            slotProps={{
              input: {
                autoComplete: "off",
                style: {
                  height: StyleSettings.control_height,
                  fontSize: StyleSettings.font,
                },
              },
            }}
            fullWidth={true}
            onChange={handleChange}
            onCompositionUpdate={handleCompositionUpdate}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            disabled={isReadOnly}
            sx={{
              backgroundColor: backgroundColor,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderWidth: borderWidth, // 테두리 두께 설정
                },
                "&:hover fieldset": {
                  borderWidth: borderWidth, // 호버 시 두께 유지
                },
                "&.Mui-focused fieldset": {
                  borderWidth: borderWidth, // 포커스 시 두께 유지
                },
              },
              "& .MuiInputBase-root": {
                paddingRight: 0,
                "&.Mui-disabled": {
                  backgroundColor: "#f5f5f5",
                },
              },
            }}
          />
        </Tooltip>
      </form>
    );
  })
);

export default SnBarcodeBox;
